import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDay } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, Eye, QrCode } from "lucide-react";
import Pagination, { PAGE_SIZE } from "@/components/Pagination";
import CardScanner from "@/components/CardScanner";

const empty = { name: "", phone: "", address: "", notes: "", referrer_code: "" };

export default function Customers() {
  const { isAdmin } = useAuth();
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [delId, setDelId] = useState(null);
  const [dupMsg, setDupMsg] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [page, setPage] = useState(1);

  const load = useCallback(async (q = "") => {
    try {
      const { data } = await api.get("/customers", { params: q ? { search: q } : {} });
      setList(data);
      setPage(1);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const paged = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ name: c.name, phone: c.phone, address: c.address || "", notes: c.notes || "", referrer_code: "" }); setEditId(c.id); setOpen(true); };

  const save = async (force = false) => {
    try {
      const cfg = force ? { params: { force: true } } : {};
      if (editId) await api.put(`/customers/${editId}`, form, cfg);
      else await api.post("/customers", form, cfg);
      toast.success("Client enregistré");
      setOpen(false); setDupMsg("");
      load(search);
    } catch (e) {
      if (e?.response?.status === 409) { setDupMsg(e.response.data?.detail || "Ce téléphone existe déjà."); return; }
      toast.error(apiError(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/customers/${delId}`);
      toast.success("Client supprimé");
      setDelId(null);
      load(search);
    } catch (e) { toast.error(apiError(e)); setDelId(null); }
  };

  const handleScan = async (text) => {
    setScanOpen(false);
    const m = (text || "").match(/\/carte\/([^/?#]+)/);
    if (m && m[1]) { nav(`/customers/${m[1]}`); return; }
    const card = (text || "").trim();
    if (!card) return toast.error("QR illisible");
    try {
      const { data } = await api.get("/customers", { params: { search: card } });
      if (data[0]) nav(`/customers/${data[0].id}`);
      else toast.error("Aucun client trouvé pour cette carte");
    } catch { toast.error("Carte introuvable"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800">Clients</h1>
        <Button data-testid="add-customer-btn" onClick={openNew} className="bg-sky-600 hover:bg-sky-700">
          <Plus size={18} className="mr-2" /> Nouveau client
        </Button>
      </div>

      <div className="flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-2.5 text-slate-400" />
          <Input
            data-testid="customer-search"
            className="pl-10"
            placeholder="Rechercher par nom, téléphone ou n° de carte..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); load(e.target.value); }}
          />
        </div>
        <Button type="button" variant="outline" data-testid="scan-card-btn" onClick={() => setScanOpen(true)} className="shrink-0"><QrCode size={16} className="mr-1.5" /> Scanner</Button>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
        <table className="data-table w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="px-4 py-3">Nom</th>
              <th className="px-4 py-3">Téléphone</th>
              <th className="px-4 py-3">N° carte</th>
              <th className="px-4 py-3 hidden md:table-cell">Adresse</th>
              <th className="px-4 py-3 hidden md:table-cell">Créé le</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-800">{c.name}</td>
                <td className="px-4 py-3">{c.phone}</td>
                <td className="px-4 py-3" data-testid={`customer-card-${c.id}`}>{c.loyalty_card_number ? <span className="text-xs font-medium bg-amber-100 text-amber-700 px-2 py-0.5 rounded">{c.loyalty_card_number}</span> : <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-3 hidden md:table-cell text-slate-500">{c.address}</td>
                <td className="px-4 py-3 hidden md:table-cell text-slate-500">{fmtDay(c.created_at)}</td>
                <td className="px-4 py-3 text-right space-x-1">
                  <Button size="icon" variant="ghost" data-testid={`view-customer-${c.id}`} onClick={() => nav(`/customers/${c.id}`)}><Eye size={16} /></Button>
                  <Button size="icon" variant="ghost" data-testid={`edit-customer-${c.id}`} onClick={() => openEdit(c)}><Pencil size={16} /></Button>
                  {isAdmin && <Button size="icon" variant="ghost" data-testid={`delete-customer-${c.id}`} onClick={() => setDelId(c.id)} className="text-red-600"><Trash2 size={16} /></Button>}
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Aucun client</td></tr>}
          </tbody>
        </table>
        </div>
      </Card>

      <Pagination page={page} total={list.length} onPage={setPage} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Modifier le client" : "Nouveau client"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom</Label><Input data-testid="customer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Téléphone</Label><Input data-testid="customer-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Adresse</Label><Input data-testid="customer-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>Notes</Label><Input data-testid="customer-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div><Label>Code parrain (optionnel)</Label><Input data-testid="customer-referrer" value={form.referrer_code} onChange={(e) => setForm({ ...form, referrer_code: e.target.value })} placeholder="ex: PA1B2C — si ce client a été parrainé" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-customer-btn" onClick={() => save()} className="bg-sky-600 hover:bg-sky-700">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce client ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-customer" onClick={remove} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!dupMsg} onOpenChange={(o) => !o && setDupMsg("")}>
        <AlertDialogContent data-testid="duplicate-phone-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Téléphone déjà utilisé</AlertDialogTitle>
            <AlertDialogDescription>{dupMsg} Souhaitez-vous quand même créer cette fiche (ex: téléphone partagé en famille) ?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-duplicate">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-duplicate" onClick={() => save(true)} className="bg-amber-500 hover:bg-amber-600">Créer quand même</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CardScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={handleScan} />
    </div>
  );
}
